import { useCallback, useEffect, useMemo, useState } from "react";

import {
  buildCellLookupTable,
  columnToNumber,
  factAddress,
  numberToColumn,
  parseCellAddress,
  sortAddresses,
  startAddress,
} from "@/jspreadsheet/utils";
import queries from "@/queries";
import { CellType, Fact, useMappingStore } from "@/stores/mapping";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { clearTableCellsTags } from "@/apis/table/route";
import {
  CellInfo,
  CellMappingInfo,
  GetTagsFromCellResponse,
  TagInfo,
  TaggedInfo,
} from "@/apis/table/types";
import { useMainStore } from "@/stores/main";
import { closeModal, modal } from "@/stores/store";
import {
  addBinding,
  getBindingContent,
  registerBindingEvent,
  removeBindingEvent,
} from "@/utils/binding";
// BindingEventArgs type removed
import { useWorkbookId } from "@/hooks/use-workbook";
import { AccountingPeriod, Accumulated, TargetYear } from "@/types/enums";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  DeleteMappingModalContent,
  TableReRegisterModalContent,
} from "./table-re-register/modal";
import {
  destructTagKey,
  isCellInRange,
  isRangeOverlapping,
  tagKey,
} from "./utils";

const hasMappedInfo = (mappingInfo?: CellMappingInfo) => {
  // null도 사용자가 확정한 값으로 본다. 이미 로컬/서버 상태가 한번 쓰인 엔트리는
  // cells()의 축약 정보로 다시 덮어쓰지 않고, 이후 상세 쿼리 응답이 보강하게 둔다.
  return Boolean(
    mappingInfo?.tags?.length ||
      mappingInfo?.accountingPeriod !== undefined ||
      mappingInfo?.targetYear !== undefined ||
      mappingInfo?.accumulated !== undefined,
  );
};

const toPartialTagInfo = (
  taggedInfo: TaggedInfo,
  roleKey: string | null,
): TagInfo => {
  const { prefix, name = taggedInfo.key } = destructTagKey(taggedInfo.key);

  return {
    id: taggedInfo.key,
    name,
    prefix,
    type: taggedInfo.type as TagInfo["type"],
    label: name,
    roleKey: roleKey ?? "",
    parent: null,
  };
};

const getCellMappingInfoFromCell = (
  cell?: CellInfo,
): CellMappingInfo | null => {
  if (!cell) return null;

  return {
    // cells()에는 key/type만 있으므로, 첫 페인트용 최소 TagInfo만 만든다.
    // label/parent 같은 상세 메타데이터는 cell tags 쿼리가 도착하면 교체된다.
    tags: cell.taggedInfos.tags.map((tag) =>
      toPartialTagInfo(tag, cell.taggedInfos.roleKey),
    ),
    accountingPeriod:
      (cell.taggedInfos.period as AccountingPeriod | null) ?? null,
    targetYear: (cell.taggedInfos.targetYear as TargetYear | null) ?? null,
    accumulated: (cell.taggedInfos.accumulated as Accumulated | null) ?? null,
  };
};

const getCellPeriodInfoFromCell = (
  cell?: CellInfo,
): GetTagsFromCellResponse | null => {
  if (!cell) return null;

  return {
    // useCellPeriod는 period 계열만 읽는다. 여기의 tags는 authoritative 데이터가 아니라
    // 첫 렌더 공백을 줄이기 위한 seed 값이라 빈 배열로 둔다.
    tags: [],
    period: (cell.taggedInfos.period as AccountingPeriod | null) ?? null,
    targetYear: (cell.taggedInfos.targetYear as TargetYear | null) ?? null,
    accumulated: (cell.taggedInfos.accumulated as Accumulated | null) ?? null,
  };
};

export const useTableAddress = () => {
  const { data: workbookId } = useWorkbookId();

  const {
    data: tableAddresses,
    isLoading,
    isError,
  } = useQuery({
    ...queries.workbook(workbookId!)._ctx.tableAddresses(),
    enabled: !!workbookId,
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
  });

  if (!workbookId) {
    return {
      tableAddresses: undefined,
      isLoading: true,
      isError: false,
    };
  }

  return {
    tableAddresses,
    isLoading,
    isError,
  };
};

export const useTableCells = () => {
  const {
    data: { workbookId, tableId, selectedFact },
  } = useTableId();
  const {
    data: cells,
    isLoading,
    isError,
  } = useQuery({
    ...queries.workbook(workbookId!)._ctx.tables(tableId!)._ctx.cells(),
    enabled: !!workbookId && !!tableId,
    staleTime: 1000 * 60 * 60 * 24,
    gcTime: 1000 * 60 * 60 * 24,
  });

  if (!workbookId) {
    return {
      cells: undefined,
      isLoading: true,
      isError: false,
    };
  }

  return {
    cells,
    isLoading,
    isError,
  };
};

const useSelectedFact = () => {
  const {
    fact: { set },
  } = useMappingStore((state) => state);

  const setSelectedFact = useCallback(
    (fact: Fact | null) => {
      // current selection 은 range일 수 있음.
      // application 내에서 계속 사용하는 fact는 단일 셀 주소로 변환해서 사용해야 함.
      // 시작 주소를 fact 주소로 변환해서 사용.
      if (fact && fact.address.includes(":")) {
        const [start, end] = fact.address.split(":");
        fact.address = start;
      }
      set(fact);
    },
    [set],
  );

  return {
    setSelectedFact,
  };
};

export const useSelectedCell = () => {
  const { current: currentSelection } = useMainStore(
    (state) => state.selection,
  );
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const { setSelectedFact } = useSelectedFact();
  const setMappedInfo = useMappingStore((state) => state.mappedInfos.set);
  const selectedMappedInfos = useMappingStore(
    (state) => state.mappedInfos.selected,
  );

  const { tableAddresses } = useTableAddress();
  const { cells } = useTableCells();
  const { data: workbookId } = useWorkbookId();
  const queryClient = useQueryClient();

  const setDimension = useMappingStore((state) => state.dimension.set);
  const dimension = useMappingStore((state) => state.dimension.dimension);
  useEffect(() => {
    if (!currentSelection || !tableAddresses) return;

    const currentTable = tableAddresses?.tableAddresses.find(
      (table) => table.tableId === currentSelection.sheet.tableId,
    );

    if (!currentTable) return;

    const firstFactOfSheet = sortAddresses(currentTable?.factAddresses)?.[0];

    const facts = currentTable?.factAddresses;

    const currentAddress = currentSelection.address;
    // 범위를 드래그한 경우 드래그 앵커(시작 셀)로 resolve. 역방향 드래그(우하단→좌상단)
    // 에서도 fact 가 시작 셀에 고정된다. 앵커가 없으면(프로그램적 선택 등) 좌상단으로 폴백.
    const resolvedAddress =
      currentSelection.anchorAddress ?? startAddress(currentAddress);
    // cells 안의 머지된 셀은 "A7:A9" 같은 범위로 들어오므로 포함 검사로 매칭
    const currentCell = cells?.cells.find((cell) => {
      const [cellStart, cellEnd = cellStart] = cell.address.split(":");
      return isCellInRange(resolvedAddress, cellStart, cellEnd);
    });
    const firstCellOfSheet = cells?.cells.find(
      (cell) => cell.address === firstFactOfSheet,
    );
    const isFirstCell = resolvedAddress === "A1";

    const isTaggableCell =
      currentCell?.type === CellType.DATA ||
      currentCell?.type === CellType.SUM ||
      currentCell?.type === CellType.HEADER ||
      currentCell?.type === CellType.NONE;

    if (dimension === "LINE_ITEM" && isTaggableCell) {
      setDimension("LINE_ITEM");
    }

    // cells 안에 존재한다 = 실제 셀(= fact 대상)이다
    const currentAddressAndCurrentIsFact = !!currentCell;
    const isNeverSelectedBeforeAndFetchFinished =
      selectedFact === null && !!firstFactOfSheet;
    const isSelectedBeforeAndCurrentIsFact =
      !!selectedFact &&
      !!currentTable &&
      currentTable.factAddresses.includes(currentAddress);
    const sheetChangedButSelectedFactExists =
      !!firstFactOfSheet &&
      currentSelection.sheet.id !== selectedFact?.sheet?.id &&
      currentSelection.address !== selectedFact?.address;
    const isEmptyFactSheet = facts?.length === 0;

    const hydrateSelectedCellState = (fact: Fact, cell?: CellInfo) => {
      const mappingInfo = getCellMappingInfoFromCell(cell);
      const mappingAddress = factAddress(fact);
      const existingMappingInfo = selectedMappedInfos[mappingAddress];

      if (mappingInfo && !hasMappedInfo(existingMappingInfo)) {
        setMappedInfo({
          address: mappingAddress,
          mappingInfo,
        });
      }

      if (!workbookId || !cell) return;

      const periodQueryKey = queries
        .workbook(workbookId)
        ._ctx.tables(currentTable.tableId)
        ._ctx.cells(fact.address)
        ._ctx.period().queryKey;

      if (!queryClient.getQueryData(periodQueryKey)) {
        const periodInfo = getCellPeriodInfoFromCell(cell);
        if (periodInfo) {
          queryClient.setQueryData(periodQueryKey, periodInfo);
        }
      }
    };

    if (isFirstCell) {
      if (!firstFactOfSheet) {
        setSelectedFact(null);
        return;
      }
      const nextFact = {
        ...currentSelection,
        sheet: currentSelection.sheet,
        address: firstFactOfSheet,
        table: { id: currentTable.tableId },
        cellType: firstCellOfSheet?.type ?? CellType.NONE,
        roleKey: currentTable.prefixRoleKey,
      };
      setSelectedFact(nextFact);
      hydrateSelectedCellState(nextFact, firstCellOfSheet);
    } else if (currentAddressAndCurrentIsFact) {
      const nextFact = {
        ...currentSelection,
        // 범위 드래그여도 fact 주소는 매칭된 셀의 실제 주소로 고정 (범위 오염 방지)
        address: currentCell?.address ?? resolvedAddress,
        table: { id: currentTable.tableId },
        cellType: currentCell?.type ?? CellType.NONE,
        roleKey: currentTable.prefixRoleKey,
      };
      setSelectedFact(nextFact);
      hydrateSelectedCellState(nextFact, currentCell);
    } else if (isNeverSelectedBeforeAndFetchFinished) {
      const nextFact = {
        address: firstFactOfSheet,
        sheet: currentSelection.sheet,
        table: { id: currentTable.tableId },
        cellType: firstCellOfSheet?.type ?? CellType.NONE,
        roleKey: currentTable.prefixRoleKey,
      };
      setSelectedFact(nextFact);
      hydrateSelectedCellState(nextFact, firstCellOfSheet);
    } else if (isSelectedBeforeAndCurrentIsFact) {
      // 이 분기 도달 시 currentCell 은 항상 undefined (위 currentAddressAndCurrentIsFact 가 선점).
      const nextFact = {
        ...currentSelection,
        cellType: CellType.NONE,
        table: { id: currentTable.tableId },
        roleKey: currentTable.prefixRoleKey,
      };
      setSelectedFact(nextFact);
      hydrateSelectedCellState(nextFact, undefined);
    } else if (sheetChangedButSelectedFactExists) {
      const nextFact = {
        address: firstFactOfSheet,
        sheet: currentSelection.sheet,
        cellType: firstCellOfSheet?.type ?? CellType.NONE,
        table: { id: currentTable.tableId },
        roleKey: currentTable.prefixRoleKey,
      };
      setSelectedFact(nextFact);
      hydrateSelectedCellState(nextFact, firstCellOfSheet);
    } else if (isEmptyFactSheet) {
      setSelectedFact(null);
    }
  }, [currentSelection, tableAddresses, cells, workbookId]);
};

export const useTagDetail = (tagKey: string, roleKey?: string) => {
  const queryClient = useQueryClient();
  const selectedFact = useMappingStore((state) => state.fact.selected?.address);

  const { data: workbookId } = useWorkbookId();

  const {
    data: { tableId },
  } = useSelectedTable();

  const {
    data: tagDetail,
    isLoading: tagDetailLoading,
    isError: tagDetailError,
  } = useQuery({
    ...queries
      .workbook(workbookId!)
      ._ctx.taxonomy()
      ._ctx.tags(tagKey, tableId!, roleKey),
    // 태그 빌딩 트리에서는 셀을 선택하지 않고도 상세를 본다 — 셀 선택을 조건으로 두지 않는다.
    enabled: !!workbookId && !!tableId && !!tagKey,
    gcTime: 1000 * 60 * 5,
    staleTime: 1000 * 60 * 5,
  });

  const invalidateTagDetail = useCallback(() => {
    if (workbookId && tableId && tagKey && selectedFact) {
      queryClient.invalidateQueries({
        queryKey: [
          ...queries.workbook(workbookId)._ctx.taxonomy()._ctx.tags._def,
        ],
      });
    }
  }, [workbookId, tableId, tagKey, selectedFact, queryClient]);

  if (!tagKey)
    return {
      data: null,
      isLoading: false,
      isError: false,
      invalidateTagDetail,
    };

  return {
    data: tagDetail ?? null,
    isLoading: tagDetailLoading,
    isError: tagDetailError,
    invalidateTagDetail,
  };
};

export const useTableId = () => {
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const { tableAddresses, isLoading, isError } = useTableAddress();
  const { data: workbookId } = useWorkbookId();
  const [searchParams] = useSearchParams();

  // URL param is the single source of truth - just read it
  const tableIdFromUrl = searchParams.get("table");

  // Fallback: derive from selectedFact
  const tableIdFromFact = useMemo(() => {
    if (!selectedFact || !tableAddresses?.tableAddresses) return null;
    const table = tableAddresses.tableAddresses.find(
      (table) => table.tableId === selectedFact.sheet?.tableId,
    );
    return table?.tableId ?? null;
  }, [selectedFact, tableAddresses]);

  const tableId = tableIdFromUrl || tableIdFromFact;

  return {
    data: {
      workbookId,
      tableId,
      selectedFact,
    },
    isLoading: isLoading,
    isError: isError,
  };
};

export const useSelectedTable = () => {
  const { tableAddresses, isLoading, isError } = useTableAddress();
  const {
    data: { tableId },
  } = useTableId();

  return {
    data: {
      tableId,
      tableAddresses,
    },
    isLoading,
    isError,
  };
};

export const useSelectedTags = (address?: string | null) => {
  const { viewTag, set: setViewTag } = useMappingStore((state) => state.detail);
  const selectedFact = useMappingStore((state) => state.fact.selected);
  const {
    selected: selectedMappedInfos,
    add,
    remove,
    set,
  } = useMappingStore((state) => state.mappedInfos);
  const { dimension } = useMappingStore((state) => state.dimension);
  const addressWithSheetId = selectedFact ? factAddress(selectedFact) : null;
  const selectedMappedInfo = useMemo(() => {
    return selectedMappedInfos[address ?? addressWithSheetId ?? ""] ?? {};
  }, [selectedMappedInfos, address, addressWithSheetId]);
  const selectedTags = selectedMappedInfo.tags ?? [];

  useEffect(() => {
    const targetType = dimension === "LINE_ITEM" ? "LINE_ITEM" : "MEMBER";
    const dimensionTags = selectedTags.filter((t) => t.type === targetType);

    if (!viewTag && dimensionTags?.length > 0) {
      setViewTag({
        key: tagKey(dimensionTags[0].prefix, dimensionTags[0].name),
        role: dimensionTags[0]?.roleKey,
      });
    } else if (!viewTag && dimensionTags?.length === 0) {
      setViewTag(null);
    }
  }, [selectedMappedInfo]);

  return { selectedMappedInfo, add, remove, set };
};

export const useCheckTableContentChanged = (
  workbookId: string,
  currentTableId: string | null,
) => {
  const { data: tableCells } = useQuery({
    ...queries.workbook(workbookId!)._ctx.tables(currentTableId!)._ctx.cells(),
    enabled: !!currentTableId,
  });

  const { tableAddresses } = useTableAddress();

  const navigate = useNavigate();

  const { mutate: clearTableCellsTagsMutation } = useMutation({
    mutationFn: async () => {
      await clearTableCellsTags(workbookId!, currentTableId!);
    },
  });
  const openReRegisterModal = (tableName: string, tableAddress: string) => {
    modal(
      <TableReRegisterModalContent
        tableName={tableName}
        tableAddress={tableAddress}
      />,
      {
        title: "표 범위 재등록",
        confirmLabel: "재등록",
        confirmButtonClassName:
          "bg-neutral-100 text-primary hover:bg-black/[0.03] active:bg-black/[0.06] focus:bg-black/[0.06] focus:shadow-button-focus",
        onConfirm: () => openDeleteModal(tableName, tableAddress),
      },
    );
  };
  const openDeleteModal = (tableName: string, tableAddress: string) => {
    modal(
      <DeleteMappingModalContent
        tableName={tableName}
        tableAddress={tableAddress}
      />,
      {
        title: "매핑 내역 삭제",
        headerClassName: "text-red-600",
        confirmLabel: "삭제",
        confirmButtonClassName:
          "bg-red-500 text-white hover:bg-red-600 active:bg-red-700 focus:bg-red-700",
        onConfirm: () => {
          clearTableCellsTagsMutation(undefined, {
            onSuccess: () => {
              navigate(
                `/register-table?mode=reregister&tableId=${currentTableId}&tableName=${tableName}&tableAddress=${tableAddress}`,
              );
            },
          });
        },
      },
    );
  };
  const hasCellDataChanged = (
    startAddress: string,
    endAddress: string,
    cellInfos: CellInfo[],
    cellValueArray: string[][],
  ) => {
    const mergedCells = cellInfos.filter((cellInfo) => {
      return cellInfo.address.includes(":");
    });

    const lookupTable = buildCellLookupTable(
      startAddress,
      endAddress,
      mergedCells.map((cellInfo) => cellInfo.address),
    );

    const { column: startColumn, row: startRow } =
      parseCellAddress(startAddress);
    const { column: endColumn, row: endRow } = parseCellAddress(endAddress);

    for (let i = startRow; i <= endRow; i++) {
      for (
        let j = columnToNumber(startColumn);
        j <= columnToNumber(endColumn);
        j++
      ) {
        const localCellValue =
          cellValueArray[i - startRow][j - columnToNumber(startColumn)];
        const cellAddress = lookupTable.get(`${numberToColumn(j)}${i}`);

        if (!cellAddress) throw new Error("cellAddress not found");
        const cellInfo = cellInfos.find(
          (cellInfo) => cellInfo.address === cellAddress,
        );
        const savedValue = cellInfo?.value || "";

        if (savedValue !== localCellValue) {
          return true;
        }
      }
    }
    return false;
  };

  useEffect(() => {
    if (!currentTableId) closeModal();
  }, [currentTableId]);
  useEffect(() => {
    const checkIfTableContentChanged = async () => {
      if (!tableCells || !tableAddresses) return;

      const data = await getBindingContent(currentTableId!, "text");
      const tableData = tableAddresses?.tableAddresses.find(
        (table) => table.tableId === currentTableId,
      );
      if (!tableData) return;
      const isTableContentChanged = hasCellDataChanged(
        tableData.start,
        tableData.end,
        tableCells.cells,
        data,
      );
      if (isTableContentChanged) {
        openReRegisterModal(
          tableData?.name!,
          `${tableData?.start}:${tableData?.end}`,
        );
      }
    };
    checkIfTableContentChanged();
  }, [tableCells]);
};

export const useGetSelectedTable = () => {
  const { tableAddresses } = useTableAddress();

  const selectedCellWithSheetName = useMainStore(
    (state) => state.selection.current?.address,
  );

  const [currentTableId, setCurrentTableId] = useState<string | null>(null);

  useEffect(() => {
    const selectedCell = selectedCellWithSheetName?.split("!")[1];
    const currentTable = tableAddresses?.tableAddresses.find(
      (table) => table.tableId === currentTableId,
    );

    if (!currentTable) {
      // closeModal();
      return;
    }
    if (selectedCell) {
      let isInsideTable = false;

      if (selectedCell.includes(":")) {
        // 범위 선택의 경우 (box)
        const [start, end] = selectedCell.split(":");
        isInsideTable = isRangeOverlapping(
          start,
          end,
          currentTable.start,
          currentTable.end,
        );
      } else {
        // 단일 셀 선택의 경우 (dot)
        isInsideTable = isCellInRange(
          selectedCell,
          currentTable.start,
          currentTable.end,
        );
      }

      if (!isInsideTable) {
        setCurrentTableId(null);
      }
    }
  }, [selectedCellWithSheetName]);

  useEffect(() => {
    if (!tableAddresses) return;
    let eventResults: any[] = [];
    const setupBinding = async () => {
      tableAddresses.tableAddresses.forEach(async (table) => {
        await addBinding(
          table.tableId,
          "Range",
          `${table.start}:${table.end}`,
          table.categoryId,
        );
      });
    };

    const registerEvents = async () => {
      eventResults = await Promise.all(
        tableAddresses.tableAddresses.map(async (table) => {
          const result = await registerBindingEvent(
            "onSelectionChanged",
            async (event) => {
              if (event.binding.id === currentTableId) return;
              setCurrentTableId(event.binding.id);
            },
            table.tableId,
          );
          return result;
        }),
      );
    };

    const fetchData = async () => {
      await setupBinding();
      await registerEvents();
    };

    fetchData();

    return () => {
      eventResults.forEach((eventResult) => removeBindingEvent(eventResult));
    };
  }, [tableAddresses]);

  return {
    currentTableId,
  };
};

export const useCheckWorkbookId = () => {
  const { data: workbookId } = useWorkbookId();
  const navigate = useNavigate();
  useEffect(() => {
    if (workbookId === null) {
      navigate("/workbooks");
    }
  }, [workbookId]);
};
